import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, ShieldAlert, RotateCcw, Search } from "lucide-react";

export default function AdminUsers() {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checked, setChecked] = useState(false);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => { if (user) api.get("/admin/whoami").then((r) => { setIsAdmin(!!r.data?.is_admin); setChecked(true); }).catch(() => setChecked(true)); }, [user]);
  const load = () => api.get("/admin/users", { params: { q: q || undefined } }).then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { if (isAdmin) { const t = setTimeout(load, 200); return () => clearTimeout(t); } }, [isAdmin, q]);

  if (!user) return <Navigate to="/login" />;
  if (checked && !isAdmin) return <div className="mx-auto max-w-lg px-6 py-24"><h1 className="text-3xl font-black tracking-tighter">Admins only.</h1></div>;

  const act = async (u, action) => {
    const reason = action === "reinstate" ? "" : (window.prompt(`Reason to ${action} ${u.display_name}?`) || "");
    if (action !== "reinstate" && reason === null) return;
    try {
      await api.post("/admin/users/action", { user_id: u.id, action, reason });
      toast.success(`${u.display_name} ${action}${action === "ban" ? "ned" : (action === "suspend" ? "ed" : "d")}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-16">
      <div className="mono text-xs text-primary flex items-center gap-2"><ShieldCheck className="h-3 w-3" />admin · moderation</div>
      <h1 className="mt-2 text-5xl font-black tracking-tighter">User accounts.</h1>

      <div className="mt-8 flex items-center gap-2 border border-border px-3 py-2 mb-6">
        <Search className="h-4 w-4 text-muted-foreground" />
        <input data-testid="admin-user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground" />
      </div>

      <div className="border border-border">
        {users.map((u, i) => (
          <div key={u.id} data-testid={`admin-user-${u.id}`} className={`flex items-center gap-3 p-4 ${i > 0 ? "border-t border-border" : ""} ${u.banned ? "opacity-60" : ""}`}>
            <div className="flex h-9 w-9 items-center justify-center bg-primary/20 text-sm font-bold text-primary">{u.display_name?.[0]?.toUpperCase()}</div>
            <div className="flex-1 min-w-0">
              <div className="font-bold tracking-tight">{u.display_name}</div>
              <div className="text-xs text-muted-foreground">{u.email} · {u.role} · {u.points ?? 0} pts</div>
              {u.suspended && <div className="mt-1 text-[10px] uppercase tracking-widest text-destructive">SUSPENDED{u.suspend_reason ? `: ${u.suspend_reason}` : ""}</div>}
              {u.banned && <div className="mt-1 text-[10px] uppercase tracking-widest text-destructive">BANNED{u.ban_reason ? `: ${u.ban_reason}` : ""}</div>}
            </div>
            <div className="flex gap-2">
              {(u.suspended || u.banned) ? (
                <Button size="sm" variant="outline" onClick={() => act(u, "reinstate")} data-testid={`reinstate-${u.id}`} className="rounded-none"><RotateCcw className="mr-1 h-3 w-3" />Reinstate</Button>
              ) : (
                <>
                  <Button size="sm" variant="outline" onClick={() => act(u, "suspend")} data-testid={`suspend-${u.id}`} className="rounded-none"><ShieldAlert className="mr-1 h-3 w-3" />Suspend</Button>
                  <Button size="sm" variant="destructive" onClick={() => act(u, "ban")} data-testid={`ban-${u.id}`} className="rounded-none"><ShieldOff className="mr-1 h-3 w-3" />Ban</Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
